import { apiClient } from './client'
import { API_ENDPOINTS } from '@/utils/constants'
import type {
  ContractTransaction,
  GroupSubscriptionBatch,
  LessonSubscriptionConsumption,
  LessonSubscriptionGrant,
  LessonSubscriptionGrantDistribution,
  LessonSubscriptionPlan,
  LessonSubscriptionType,
  LessonPaymentMethod,
} from '@/types/billing'

type ApiResponse<T> = { message?: string; data?: T }

function unwrapResource<T>(raw: ApiResponse<T> | T): T {
  if (raw && typeof raw === 'object' && 'data' in raw) {
    return (raw as ApiResponse<T>).data as T
  }
  return raw as T
}

export type ContractSubscriptionsResponse = {
  /** Grant, активный сегодня (сводка) */
  current: LessonSubscriptionGrant | null
  /** Пагинированный список всех grants (starts_at desc) */
  grants: LessonSubscriptionGrant[]
  meta: {
    current_page: number
    per_page: number
    total: number
    last_page: number
  }
}

export type GroupSubscriptionsResponse = {
  active: LessonSubscriptionGrant[]
  history: LessonSubscriptionGrant[]
  batches: GroupSubscriptionBatch[]
}

export type GroupSubscriptionBatchDetail = {
  batch_id: string
  starts_at?: string | null
  expires_at?: string | null
  plan?: LessonSubscriptionPlan | null
  grants: LessonSubscriptionGrant[]
  distributions: LessonSubscriptionGrantDistribution[]
}

export const lessonSubscriptionsApi = {
  listPlans: async (params?: {
    is_active?: boolean
    subscription_type?: LessonSubscriptionType
    page?: number
    per_page?: number
  }): Promise<{ data: LessonSubscriptionPlan[]; total: number; last_page: number }> => {
    const response = await apiClient.get<{
      data?: LessonSubscriptionPlan[]
      meta?: { total?: number; last_page?: number }
    }>(API_ENDPOINTS.LESSON_SUBSCRIPTION_PLANS, { params })
    return {
      data: response.data?.data ?? [],
      total: response.data?.meta?.total ?? 0,
      last_page: response.data?.meta?.last_page ?? 1,
    }
  },

  createPlan: async (
    payload: Omit<LessonSubscriptionPlan, 'id' | 'teacher_id' | 'is_active' | 'total_price' | 'created_at' | 'updated_at'> & {
      description?: string | null
    }
  ): Promise<LessonSubscriptionPlan> => {
    const response = await apiClient.post<ApiResponse<LessonSubscriptionPlan>>(
      API_ENDPOINTS.LESSON_SUBSCRIPTION_PLANS,
      payload
    )
    return unwrapResource(response.data)
  },

  updatePlan: async (
    planId: number,
    payload: Partial<Pick<LessonSubscriptionPlan, 'title' | 'description' | 'is_active' | 'lessons_count' | 'duration_days' | 'price_per_lesson'>>
  ): Promise<LessonSubscriptionPlan> => {
    const response = await apiClient.patch<ApiResponse<LessonSubscriptionPlan>>(
      API_ENDPOINTS.LESSON_SUBSCRIPTION_PLAN(planId),
      payload
    )
    return unwrapResource(response.data)
  },

  deletePlan: async (planId: number): Promise<void> => {
    await apiClient.delete(API_ENDPOINTS.LESSON_SUBSCRIPTION_PLAN(planId))
  },

  listForContract: async (
    contractId: number,
    params?: { page?: number; per_page?: number }
  ): Promise<ContractSubscriptionsResponse> => {
    const response = await apiClient.get<ApiResponse<ContractSubscriptionsResponse>>(
      API_ENDPOINTS.CONTRACTS.LESSON_SUBSCRIPTIONS(contractId),
      { params }
    )
    const data = unwrapResource(response.data)
    return {
      current: data.current ?? null,
      grants: data.grants ?? [],
      meta: data.meta ?? { current_page: 1, per_page: 10, total: 0, last_page: 1 },
    }
  },

  /** Активные индивидуальные планы учителя договора (для покупки абонемента) */
  listAvailablePlans: async (contractId: number): Promise<LessonSubscriptionPlan[]> => {
    const response = await apiClient.get<ApiResponse<LessonSubscriptionPlan[]>>(
      API_ENDPOINTS.CONTRACTS.AVAILABLE_PLANS(contractId)
    )
    return unwrapResource(response.data) ?? []
  },

  /** Заявка ученика на покупку абонемента → pending-транзакция subscription_purchase */
  requestPurchase: async (
    contractId: number,
    payload: { plan_id: number; starts_at: string }
  ): Promise<ContractTransaction> => {
    const response = await apiClient.post<ApiResponse<ContractTransaction>>(
      API_ENDPOINTS.CONTRACTS.LESSON_SUBSCRIPTIONS_REQUEST(contractId),
      payload
    )
    return unwrapResource(response.data)
  },

  /** История списаний занятий по grant */
  listConsumptions: async (grantId: number): Promise<LessonSubscriptionConsumption[]> => {
    const response = await apiClient.get<ApiResponse<LessonSubscriptionConsumption[]>>(
      API_ENDPOINTS.LESSON_SUBSCRIPTION_GRANT_CONSUMPTIONS(grantId)
    )
    return unwrapResource(response.data) ?? []
  },

  issueForContract: async (
    contractId: number,
    payload: {
      plan_id: number
      starts_at: string
      expires_at?: string | null
      payment_method: LessonPaymentMethod
    }
  ): Promise<{ grant: LessonSubscriptionGrant }> => {
    const response = await apiClient.post<ApiResponse<{ grant: LessonSubscriptionGrant }>>(
      API_ENDPOINTS.CONTRACTS.LESSON_SUBSCRIPTIONS(contractId),
      payload
    )
    return unwrapResource(response.data)
  },

  cancelGrant: async (grantId: number, reason?: string): Promise<LessonSubscriptionGrant> => {
    const response = await apiClient.post<ApiResponse<LessonSubscriptionGrant>>(
      API_ENDPOINTS.LESSON_SUBSCRIPTION_GRANT_CANCEL(grantId),
      reason ? { reason } : {}
    )
    return unwrapResource(response.data)
  },

  listForGroup: async (groupId: number): Promise<GroupSubscriptionsResponse> => {
    const response = await apiClient.get<ApiResponse<GroupSubscriptionsResponse>>(
      API_ENDPOINTS.GROUP_BILLING.LESSON_SUBSCRIPTIONS(groupId)
    )
    const data = unwrapResource(response.data)
    return {
      active: data.active ?? [],
      history: data.history ?? [],
      batches: data.batches ?? [],
    }
  },

  getGroupBatch: async (groupId: number, batchId: string): Promise<GroupSubscriptionBatchDetail> => {
    const response = await apiClient.get<ApiResponse<GroupSubscriptionBatchDetail>>(
      API_ENDPOINTS.GROUP_BILLING.LESSON_SUBSCRIPTION_BATCH(groupId, batchId)
    )
    return unwrapResource(response.data)
  },

  issueForGroup: async (
    groupId: number,
    payload: {
      plan_id: number
      starts_at: string
      expires_at?: string | null
      payment_method: LessonPaymentMethod
    }
  ): Promise<{
    batch_id: string
    grants: LessonSubscriptionGrant[]
    distributions: LessonSubscriptionGrantDistribution[]
  }> => {
    const response = await apiClient.post<
      ApiResponse<{
        batch_id: string
        grants: LessonSubscriptionGrant[]
        distributions: LessonSubscriptionGrantDistribution[]
      }>
    >(API_ENDPOINTS.GROUP_BILLING.LESSON_SUBSCRIPTIONS(groupId), payload)
    return unwrapResource(response.data)
  },
}
